import React, { useRef } from 'react';

export interface TomeTabItem<Id extends string = string> {
  id: Id;
  label: React.ReactNode;
}

export interface TomeTabsProps<Id extends string = string> {
  tabs: readonly TomeTabItem<Id>[];
  active: Id;
  onSelect: (id: Id) => void;
  /** The tablist's accessible name. */
  label: string;
  /** Ties each tab to its panel: `${idPrefix}-tab-${id}` controls `${idPrefix}-panel-${id}`. */
  idPrefix: string;
  className?: string;
}

/** Page tabs inked for parchment. Arrow keys, Home and End move between them, as the ARIA tabs pattern asks. */
export function TomeTabs<Id extends string = string>({ tabs, active, onSelect, label, idPrefix, className = '' }: TomeTabsProps<Id>): React.ReactElement {
  const refs = useRef(new Map<Id, HTMLButtonElement>());

  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const current = tabs.findIndex(tab => tab.id === active);
    const next = event.key === 'ArrowRight' ? (current + 1) % tabs.length
      : event.key === 'ArrowLeft' ? (current - 1 + tabs.length) % tabs.length
        : event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : null;
    if (next === null) return;
    event.preventDefault();
    onSelect(tabs[next].id);
    refs.current.get(tabs[next].id)?.focus();
  };

  return <div role="tablist" aria-label={label} className={`tome-tabs ${className}`}>
    {tabs.map(tab => <button key={tab.id} ref={element => { if (element) refs.current.set(tab.id, element); else refs.current.delete(tab.id); }}
      id={`${idPrefix}-tab-${tab.id}`} type="button" role="tab" className="tome-tab" aria-selected={tab.id === active}
      aria-controls={`${idPrefix}-panel-${tab.id}`} tabIndex={tab.id === active ? 0 : -1}
      onClick={() => onSelect(tab.id)} onKeyDown={onKeyDown}>{tab.label}</button>)}
  </div>;
}
